/**
 * Select.
 *
 * A native `<select>` deliberately, not a custom listbox. It is keyboard
 * accessible for free, it works on a phone (which is where this app will be
 * read half the time), and it inherits the theme through `color-scheme`,
 * which the theme store sets on `<html>` for exactly this reason.
 */

import { ChevronDown } from 'lucide-react';
import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

import { Field } from './Input';
import { FIELD_SIZES, type FieldSize } from './types';

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface SelectGroup<T extends string = string> {
  label: string;
  options: ReadonlyArray<SelectOption<T>>;
}

export interface SelectProps<T extends string = string>
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value' | 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  value: T;
  onChange: (value: T) => void;
  options?: ReadonlyArray<SelectOption<T>>;
  groups?: ReadonlyArray<SelectGroup<T>>;
  /** Shown as a disabled first entry when `value` is empty. */
  placeholder?: string;
  /** The shared control scale. Matches Button and Input at every step. */
  size?: FieldSize;
  /** @deprecated Use `size`. Kept so existing call sites keep compiling. */
  selectSize?: FieldSize;
  containerClassName?: string;
}

function SelectInner<T extends string = string>(
  {
    label,
    hint,
    error,
    value,
    onChange,
    options,
    groups,
    placeholder,
    size,
    selectSize,
    className,
    containerClassName,
    id,
    ...rest
  }: SelectProps<T>,
  ref: React.ForwardedRef<HTMLSelectElement>,
) {
  const generated = useId();
  const selectId = id ?? generated;
  const resolvedSize: FieldSize = size ?? selectSize ?? 'md';

  const renderOptions = (list: ReadonlyArray<SelectOption<T>>) =>
    list.map((option) => (
      <option key={option.value} value={option.value} disabled={option.disabled}>
        {option.label}
      </option>
    ));

  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={rest.required}
      htmlFor={selectId}
      className={containerClassName}
    >
      <div className="relative flex items-center">
        <select
          ref={ref}
          id={selectId}
          value={value}
          aria-invalid={error ? true : undefined}
          onChange={(event) => onChange(event.target.value as T)}
          className={cn(
            'w-full appearance-none rounded border border-border bg-surface-inset pr-7 pl-2 text-hi',
            'transition-colors outline-none focus:border-accent',
            'disabled:cursor-not-allowed disabled:opacity-50',
            FIELD_SIZES[resolvedSize],
            error && 'border-danger focus:border-danger',
            className,
          )}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {groups
            ? groups.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {renderOptions(group.options)}
                </optgroup>
              ))
            : renderOptions(options ?? [])}
        </select>
        <ChevronDown
          size={13}
          className="pointer-events-none absolute right-2 text-lo"
          aria-hidden
        />
      </div>
    </Field>
  );
}

export const Select = forwardRef(SelectInner) as <T extends string = string>(
  props: SelectProps<T> & { ref?: React.ForwardedRef<HTMLSelectElement> },
) => ReturnType<typeof SelectInner>;
